import clsx from 'clsx';
import { useForm } from '../../../hooks/useForm';
import { Button } from '../../../components/Button/NewButton';
import { useSort } from '../../../hooks/useSort';
import { Input } from '../../../components/Input';
import React, { ReactNode } from 'react';

interface FormInputRow<T extends object> {
  order: number;
  rowInputs: FormInput<T>[];
}

interface DefaultInputProps<T extends object> {
  disabled?: boolean;
  label?: string;
  name: keyof T;
  required: boolean;
  value(values: T): any;
  inputProps?: any;
}

interface FormInput<T extends object> {
  customInput?(
    values: T,
    updateField: (field: keyof T, value: any) => void,
    variant: 'default' | 'contained'
  ): ReactNode;
  props?: DefaultInputProps<T>;
}

interface Props<T extends object> {
  className?: string;
  defaultForm: T;
  inputs: FormInputRow<T>[];
  onSubmit(values: T): void;
  submitButton?: {
    theme?: 'action' | 'default' | 'text';
    text?: string;
  };
  validation(values: T): boolean;
  variant?: 'default' | 'contained';
  width?: string;
}

export function DynamicForm<T extends object>({
  className,
  defaultForm,
  inputs: inputRows,
  onSubmit,
  submitButton = { theme: 'default', text: 'Save' },
  validation,
  variant = 'contained',
  width = 'w-4/5',
}: Props<T>) {
  const {
    values,
    onChange,
    onSubmit: onFormSubmit,
    resetForm,
    updateField,
  } = useForm<T>(defaultForm, () => {
    onSubmit(values);
    resetForm();
  });
  const { sortAsc } = useSort<{ order: number }>('order');
  const classes = useStyles();

  return (
    <fieldset className={clsx(className, width, classes.form)}>
      {inputRows.sort(sortAsc).map((row, index) => (
        <div key={`form-row-${index + 1}`} className={classes.row}>
          {row.rowInputs.map(({ customInput, props }, index) => {
            if (customInput !== undefined)
              return (
                <React.Fragment key={`form-input-${index + 1}`}>
                  {customInput(values, updateField, variant)}
                </React.Fragment>
              );
            else if (props !== undefined) {
              const { name, value, inputProps, ...rest } = props;
              return (
                <Input
                  key={`form-input-${index + 1}`}
                  name={name}
                  onChange={onChange}
                  variant={variant}
                  value={value(values)}
                  {...inputProps}
                  {...rest}
                />
              );
            } else return;
          })}
        </div>
      ))}
      <Button
        className="ml-auto"
        disabled={validation(values) === false}
        theme={submitButton.theme ?? 'default'}
        // type="submit"
        onClick={() => {
          onSubmit(values);
          resetForm();
        }}
      >
        {submitButton.text ?? 'Save'}
      </Button>
    </fieldset>
  );
}

const useStyles = () => ({
  form: 'flex flex-col items-center gap-4',
  row: 'inline-flex items-center gap-4 w-full',
});
